/**
 * @module TabLock
 * Runs work while holding a Web Lock shared by every tab, letting a visible tab steal it.
 * The lock only reduces duplicate work, and a stolen operation is signaled but keeps running until it stops itself.
 * Operations must be safe to run more than once, including at the same time in two tabs.
 */

// Abort signals that fire when another tab steals a lock this tab holds, keyed by lock name
/** @type {Map<string, AbortSignal>} */
const activeLockAbortSignals = new Map();

/**
 * Run an operation while holding the named lock, stealing it in a visible tab and skipping it when held otherwise.
 * @param {string} lockName
 * @param {() => Promise<any>} operation Looks up its abort signal with getActiveLockAbortSignal.
 * @returns {Promise<{hasKeptLock: boolean, operationResult: any}>} Whether this tab held the lock until the operation ended, and the value it resolved to.
 */
export async function runWhileHoldingTabLock(lockName, operation) {
  // Steal the lock while the tab is visible and take it only when free otherwise
  const isTabVisible = globalThis.document?.visibilityState === 'visible';
  const lockOptions = isTabVisible ? { steal: true } : { ifAvailable: true };

  // Run the operation once the lock is granted
  return await requestTabLockAndRunOperation(lockName, lockOptions, operation);
}

/**
 * Get the abort signal that fires when another tab steals the named lock from this tab.
 * @param {string} lockName
 * @returns {AbortSignal|null} The signal for this tab's hold, or null when this tab does not hold the lock.
 */
export function getActiveLockAbortSignal(lockName) {
  return activeLockAbortSignals.get(lockName) ?? null;
}

/**
 * Check whether another tab stole the named lock from this tab's running operation.
 * @param {string} lockName
 * @returns {boolean} True when this tab's hold on the lock was stolen.
 */
export function checkTabLockIsStolen(lockName) {
  const lockAbortSignal = getActiveLockAbortSignal(lockName);
  return lockAbortSignal?.aborted === true;
}

/**
 * Request the named lock and run an operation once it is granted, signaling it if another tab takes the lock.
 * @param {string} lockName
 * @param {LockOptions} lockOptions
 * @param {() => Promise<any>} operation
 * @returns {Promise<{hasKeptLock: boolean, operationResult: any}>} Whether this tab held the lock until the operation ended, and the value it resolved to.
 */
async function requestTabLockAndRunOperation(lockName, lockOptions, operation) {
  // Prefix the lock name because Web Locks rejects names starting with a dash
  const webLockName = `tab-lock/${lockName}`;

  // Signal the operation when another tab steals the lock
  const lockStolenController = new AbortController();

  // Run the operation unlocked on browsers without Web Locks
  const lockManager = globalThis.navigator?.locks;
  if (!lockManager) {
    const operationResult = await runOperationAsLockHolder(lockName, operation, lockStolenController.signal);
    return { hasKeptLock: true, operationResult };
  }

  // Start the operation from the callback the browser runs once it decides on the lock
  /** @type {Promise<any>|null} */
  let operationPromise = null;
  /** @param {Lock|null} lock */
  const startOperationIfLockIsGranted = (lock) => {
    // The browser passes null when an ifAvailable request finds the lock held
    if (lock === null) return;
    operationPromise = runOperationAsLockHolder(lockName, operation, lockStolenController.signal);

    // The browser holds the lock until this settles, leaving operation errors to the caller
    return operationPromise.catch(() => {});
  };

  // Request the lock and signal the still running operation when another tab steals it
  try {
    await lockManager.request(webLockName, lockOptions, startOperationIfLockIsGranted);
  } catch (error) {
    if (!operationPromise) throw error;
    lockStolenController.abort(error);
  }

  // Report the lock not granted, or wait for the operation and report whether this tab kept the lock
  if (!operationPromise) return { hasKeptLock: false, operationResult: null };
  const operationResult = await operationPromise;
  const hasKeptLock = !lockStolenController.signal.aborted;
  return { hasKeptLock, operationResult };
}

/**
 * Run an operation as the lock holder, keeping its abort signal in the active lock abort signals until it ends.
 * @param {string} lockName
 * @param {() => Promise<any>} operation
 * @param {AbortSignal} lockStolenSignal Aborts when another tab steals the lock.
 * @returns {Promise<any>} The value the operation resolves to.
 */
async function runOperationAsLockHolder(lockName, operation, lockStolenSignal) {
  // Keep the abort signal for callers to look up while the operation runs
  activeLockAbortSignals.set(lockName, lockStolenSignal);

  try {
    return await operation();
  } finally {
    // Remove the abort signal unless a newer hold already replaced it
    const isAbortSignalStillActive = activeLockAbortSignals.get(lockName) === lockStolenSignal;
    if (isAbortSignalStillActive) activeLockAbortSignals.delete(lockName);
  }
}
